import styles from "./AddNewMessage.module.scss";
import classNames from "classnames/bind";
import { useState, useEffect } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import BaseModal from "~/components/common/base-modal/BaseModal";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleSendSms } from "~/thunks/sms/smsThunk";
import { notify } from "~/utils/helper";

const cx = classNames.bind(styles);

interface AddNewMessageProps {
  open: boolean;
  onClose: () => void;
  defaultContactPhone?: string;
}

const AddNewMessage = ({ open, onClose, defaultContactPhone }: AddNewMessageProps) => {
  const dispatch = useAppDispatch();
  const { sendingSms } = useAppSelector((state) => state.sms);

  const [formData, setFormData] = useState({
    contactPhone: defaultContactPhone || "",
    body: "",
  });

  const [errors, setErrors] = useState({
    contactPhone: "",
    body: "",
  });

  useEffect(() => {
    if (open) {
      setFormData({
        contactPhone: defaultContactPhone || "",
        body: "",
      });
      setErrors({
        contactPhone: "",
        body: "",
      });
    }
  }, [open, defaultContactPhone]);

  const validateForm = (): boolean => {
    let hasError = false;
    const newErrors = { contactPhone: "", body: "" };

    // Validate phone number
    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = "Phone number is required";
      hasError = true;
    } else {
      // E.164 format validation
      const e164Regex = /^\+[1-9]\d{1,14}$/;
      if (!e164Regex.test(formData.contactPhone.trim())) {
        newErrors.contactPhone = "Phone number must be in E.164 format (e.g., +1234567890)";
        hasError = true;
      }
    }

    // Validate message body
    if (!formData.body.trim()) {
      newErrors.body = "Message body is required";
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const handleCompose = async () => {
    if (!validateForm()) return;

    try {
      await dispatch(
        handleSendSms({
          to: formData.contactPhone.trim(),
          body: formData.body.trim(),
        })
      ).unwrap();

      notify("success", "SMS sent successfully!");
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to send SMS";
      notify("error", errorMessage);
    }
  };

  return (
    <BaseModal open={open} onClose={onClose} width="90%" maxWidth="550px">
      <div className={cx("compose-modal-container")}>
        <div className={cx("compose-modal-header")}>
          <h3 className={cx("compose-modal-title")}>Compose New Message</h3>
        </div>

        <div className={cx("compose-modal-content")}>
          <div className={cx("compose-modal-field")}>
            <label className={cx("compose-modal-label")}>
              Contact or phone number
            </label>
            <PrimaryInput
              name="contactPhone"
              placeholder="Contact or phone number (E.164 format: +1234567890)"
              value={formData.contactPhone}
              onChange={(e) =>
                setFormData({ ...formData, contactPhone: e.target.value })
              }
              className={cx("compose-input")}
            />
            {errors.contactPhone && (
              <span className={cx("error-message")}>{errors.contactPhone}</span>
            )}
          </div>

          <div className={cx("compose-modal-field")}>
            <label className={cx("compose-modal-label")}>Message</label>
            <textarea
              name="body"
              placeholder="Type your message here..."
              value={formData.body}
              onChange={(e) =>
                setFormData({ ...formData, body: e.target.value })
              }
              className={cx("compose-textarea")}
              rows={5}
            />
            {errors.body && (
              <span className={cx("error-message")}>{errors.body}</span>
            )}
          </div>
        </div>

        <div className={cx("compose-modal-footer")}>
          <PrimaryButton
            title="Cancel"
            onClick={onClose}
            className={cx("compose-cancel-button")}
            disabled={sendingSms}
          />
          <PrimaryButton
            title={sendingSms ? "Sending..." : "Send"}
            onClick={handleCompose}
            className={cx("compose-submit-button")}
            disabled={sendingSms}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default AddNewMessage;
